import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { slugify } from '@/lib/slug';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: { name: string; slug: string; plan?: string }) => Promise<void> | void;
  pending: boolean;
}

export function CreateWorkspaceDialog({ open, onClose, onSubmit, pending }: Props) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setSlug('');
      setSlugTouched(false);
    }
  }, [open]);

  function onNameChange(value: string) {
    setName(value);
    if (!slugTouched) setSlug(slugify(value));
  }

  const canSubmit = name.trim().length >= 2 && slug.trim().length >= 2 && !pending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New workspace"
      description="Workspaces own projects, members, and languages."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              void onSubmit({
                name: name.trim(),
                slug: slug.trim(),
              })
            }
            disabled={!canSubmit}
          >
            {pending ? 'Creating…' : 'Create workspace'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) void onSubmit({ name: name.trim(), slug: slug.trim() });
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="ws-name">Name</Label>
          <Input
            id="ws-name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="CoreFi"
            autoFocus
            required
            minLength={2}
            maxLength={120}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ws-slug">Slug</Label>
          <Input
            id="ws-slug"
            value={slug}
            onChange={(e) => {
              setSlug(slugify(e.target.value));
              setSlugTouched(true);
            }}
            placeholder="corefi"
            required
            minLength={2}
            maxLength={64}
            pattern="[a-z0-9][a-z0-9-]*"
          />
          <p className="text-[11px] text-muted-foreground">
            Lowercase letters, digits, and dashes. Used in URLs and CLI commands.
          </p>
        </div>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
